/* ==========================================================================
   BOUNTEOUS CONFIDENTIAL
   ___________________

   Copyright 2019 Bounteous
   All Rights Reserved.

   NOTICE:  All information contained herein is, and remains the property
   of Bounteous and its suppliers, if any. The intellectual and
   technical concepts contained herein are proprietary to Bounteous
   and its suppliers and are protected by trade secret or copyright law.
   Dissemination of this information or reproduction of this material
   is strictly forbidden unless prior written permission is obtained
   from Bounteous.
   ========================================================================== */

import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter, setDataAttr, uuid } from 'js/helpers.js';

const accordionSelector = '.cmp-accordion';
const accordionItemName = 'cmp-accordion-item';
const headerClassStr = `${accordionItemName}__header`;
const contentClassStr = `${accordionItemName}__content`;
const imageClassStr = `${accordionItemName}__image`;

class Accordion extends Component {

    /**
     * Set IDs on Accordion items for aria-controls
     */
    setIds() {
        [...this.elements.accordionItems].map((ele) => {
            const headerEle = ele.getElementsByClassName(headerClassStr)[0];
            const contentEle = ele.getElementsByClassName(contentClassStr)[0];
            const eleUuid = uuid().replace(/-/g, '');

            setDataAttr(headerEle, 'aria-controls', eleUuid);
            contentEle.id = eleUuid;
            return null;
        });
    }

    /**
     * Set to collapse all accordion elements
     */
    setCollapse() {
        [...this.elements.accordionItems].map((ele) => {
            const scopedEle = ele;
            this.toggle(ele, false);
            scopedEle.style.opacity = 1;
            return null;
        });
    }

    /**
     * Toggle Accordion Item (expand/collapse)
     * @param {DOM Element} accordionItem - Accordion Item Element
     * @param {bool} directive - true = expand accordion | false = collapse accordion
     */
    toggle(accordionItem, directive) {
        if (accordionItem) {
            const headerEle = accordionItem.getElementsByClassName(headerClassStr)[0];
            const contentEle = accordionItem.getElementsByClassName(contentClassStr)[0];
            const imageEle = accordionItem.getElementsByClassName(imageClassStr)[0];

            if (directive) {
                // Expand Accordion
                if (!isEditModeFooter() && accordionItem.closest(accordionSelector).classList.contains('two-column')) {
                    this.setCollapse();
                }
                setDataAttr(headerEle, 'aria-expanded', 'true');
                setDataAttr(contentEle, 'aria-hidden', 'false');
                setDataAttr(imageEle, 'aria-hidden', 'false');
            } else {
                // Collapse Accordion
                setDataAttr(headerEle, 'aria-expanded', 'false');
                setDataAttr(contentEle, 'aria-hidden', 'true');
                setDataAttr(imageEle, 'aria-hidden', 'true');
            }
        }
    }

    toggleButtons(openButton, closeButton, accordionItems) {
        closeButton.classList.remove('hide');
        openButton.classList.add('hide');
        accordionItems.forEach((item) => {
            if (item.querySelector(`.${headerClassStr}`).getAttribute('aria-expanded') === 'false') {
                closeButton.classList.add('hide');
                openButton.classList.remove('hide');
            }
        });
    }

    /**
     * Expand Accordions in Edit Mode
     */
    editModeExpand() {
        [...this.elements.accordionItems].map((ele) => {
            this.toggle(ele, true);
            return null;
        });
    }

    /**
     * Expand Initial Accordion Item (if author option set)
     */
    initialExpand() {
        let initialExpand = this.element.dataset.initiallyExpandedItem;
        if (initialExpand) {
            initialExpand = parseInt(initialExpand, 10) - 1;
            this.toggle(this.elements.accordionItems[initialExpand], true);
        }
    }

    /**
     * Event Listeners
     */
    addEventListeners() {
        // Accordion Item Header Click
        [...this.elements.accordionItems].map((ele) => {
            const headerEle = ele.querySelector(`.${headerClassStr}`);
            if (headerEle) {
                headerEle.addEventListener('click', () => {
                    const isExpanded = headerEle.getAttribute('aria-expanded') === 'true';
                    if (isExpanded) {
                        this.toggle(ele, false);
                    } else {
                        this.toggle(ele, true);
                    }
                    if (this.elements.expandButton !== null) {
                        this.toggleButtons(this.elements.expandButton, this.elements.collapseButton, [...this.elements.accordionItems]);
                    }
                });
            }
            return null;
        });

        // Add Event Listeners
        if (this.elements.expandButton !== null) {
            this.elements.expandButton.addEventListener('click', () => {
                [...this.elements.accordionItems].forEach((accordionItem) => {
                    this.toggle(accordionItem, true);
                });
                this.toggleButtons(this.elements.expandButton, this.elements.collapseButton, [...this.elements.accordionItems]);
            });
        }

        if (this.elements.collapseButton !== null) {
            this.elements.collapseButton.addEventListener('click', () => {
                [...this.elements.accordionItems].forEach((accordionItem) => {
                    this.toggle(accordionItem, false);
                });
                this.toggleButtons(this.elements.expandButton, this.elements.collapseButton, [...this.elements.accordionItems]);
            });
        }
    }

    /**
     * Initialize Component
     */
    init() {
        // Set up Data
        this.maxHeightStr = '0';

        // Set up Elements
        this.elements = {
            accordionItems: this.element.getElementsByClassName(accordionItemName),
            expandButton: this.element.previousElementSibling ? this.element.previousElementSibling.querySelector(`${accordionSelector}__expand-all-btn`) : null,
            collapseButton: this.element.previousElementSibling ? this.element.previousElementSibling.querySelector(`${accordionSelector}__collapse-all-btn`) : null
        };

        // Fire Init Funcs
        if (!isEditModeFooter()) {
            // Production Mode
            this.setIds();
            this.setCollapse();
            this.initialExpand();
            this.addEventListeners();
        } else {
            // Edit Mode
            this.setCollapse();
            this.editModeExpand();
        }
    }
}

registerComponent(accordionSelector, Accordion);
